import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { base44 } from "api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import {
  Plus, Package, Trash2, AlertCircle, Pencil, AlertTriangle,
  CheckCircle, ShoppingBag, TrendingDown, Building2,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Download, Search, BarChart3,
} from "lucide-react";
import { Badge } from "components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import { format, addDays, startOfWeek, endOfWeek } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  buildInventoryItemPayload,
  CANONICAL_INVENTORY_UNIT_OPTIONS,
  calculateProjectedStockAlerts,
  formatInventoryNumber,
  getReorderRatio,
  getDerivedCostPerUnit,
  getOrderDraft,
  getPurchasePlan,
  getPurchaseUnitLabel,
  groupOrdersBySupplier,
  isBelowReorderPoint,
  normalizeInventoryUnit,
  normalizeInventoryItemForm,
  parseLocaleNumber,
  buildOrderListPdfBytes,
  buildOrderListPdfFilename,
  formatOrderListQuantity,
} from "utils";

const FORECAST_HORIZON_DAYS = 14;

function isMissingCost(item) {
  const cost = parseLocaleNumber(item?.cost_per_unit);
  return item?.cost_per_unit === null
    || item?.cost_per_unit === undefined
    || item?.cost_per_unit === ''
    || !Number.isFinite(cost)
    || cost <= 0;
}

// ─── New Order Form default ───
const EMPTY_ORDER_FORM = {
  inventory_item_id: '',
  inventory_item_name: '',
  article_number: '',
  package_label: '',
  suggested_quantity: '',
  unit: '',
  supplier_id: '',
  supplier: '',
  order_date: format(new Date(), 'yyyy-MM-dd'),
  expected_delivery_date: '',
  estimated_cost: '',
  status: '',
  notes: '',
};

// ─── Stock bar helper ───
function StockBar({ current, reorder, unit }) {
  const pct = reorder > 0 ? Math.min((current / reorder) * 100, 100) : 100;
  const color = pct < 30 ? 'bg-red-500' : pct < 60 ? 'bg-orange-400' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 sm:w-24 h-2.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-600 whitespace-nowrap">
        {formatInventoryNumber(current)} {unit} / {formatInventoryNumber(reorder)} {unit}
      </span>
    </div>
  );
}

function formatForecastQuantity(value) {
  const number = parseLocaleNumber(value);
  if (!Number.isFinite(number)) return '0';
  if (number >= 10) return String(Math.round(number));
  return number.toFixed(1).replace(/\.0$/, '');
}

function getForecastConfidenceLabel(confidence, t) {
  const key = String(confidence || 'none').toLowerCase();
  return t(`stock.itemForecastConfidence.${key}`, t('stock.itemForecastConfidence.none'));
}

function getForecastConfidenceClass(confidence) {
  const key = String(confidence || 'none').toLowerCase();
  if (key === 'high') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (key === 'medium') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (key === 'low') return 'bg-red-50 text-red-700 border border-red-200';
  return 'bg-slate-100 text-slate-600 border border-slate-200';
}

const FORECAST_WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function getForecastDayLabel(day, t) {
  const backendDow = Number(day?.day_of_week);
  if (Number.isInteger(backendDow) && backendDow >= 0 && backendDow <= 6) {
    return t(`stock.itemForecastWeekdays.${FORECAST_WEEKDAY_KEYS[backendDow]}`);
  }

  if (day?.date) {
    const parsedDate = new Date(`${day.date}T00:00:00`);
    if (!Number.isNaN(parsedDate.getTime())) {
      const mondayFirstIndex = (parsedDate.getDay() + 6) % 7;
      return t(`stock.itemForecastWeekdays.${FORECAST_WEEKDAY_KEYS[mondayFirstIndex]}`);
    }
  }

  return day?.date || '';
}

function normalizeForecastProductName(value, { loose = false } = {}) {
  let normalized = `${value ?? ""}`
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ");

  if (loose) {
    normalized = normalized
      .replace(/\b\d+([.,]\d+)?\b/g, " ")
      .replace(/\b(ml|cl|l|kg|g|gr|stk|stuck|stueck|pcs|pc|piece|portion|portions|x)\b/g, " ");
  }

  return normalized.replace(/\s+/g, " ").trim();
}

function hasSpecificSizeToken(value) {
  return /\d/.test(`${value ?? ""}`);
}

function getForecastItemOptionIdentity(item) {
  const name = item?.name || "";
  const exactKey = normalizeForecastProductName(name);
  const looseKey = normalizeForecastProductName(name, { loose: true });
  return {
    key: exactKey || looseKey,
    exactKey,
    looseKey,
    name,
    allowLooseMatch: !hasSpecificSizeToken(name),
  };
}

function forecastItemMatchesOption(name, option) {
  const exactKey = normalizeForecastProductName(name);
  if (exactKey && exactKey === option.exactKey) return true;
  if (!option.allowLooseMatch) return false;
  const looseKey = normalizeForecastProductName(name, { loose: true });
  return Boolean(looseKey && looseKey === option.looseKey);
}

function buildForecastItemOptions(forecasts, sales) {
  const optionsByKey = new Map();

  (forecasts || []).forEach((day) => {
    (day.items || []).forEach((item) => {
      const identity = getForecastItemOptionIdentity(item);
      if (!identity.key) return;
      const existing = optionsByKey.get(identity.key) || {
        ...identity,
        forecastQuantity: 0,
        actualQuantity: 0,
        hasForecast: false,
      };
      existing.forecastQuantity += Number(item.quantity) || 0;
      existing.hasForecast = true;
      optionsByKey.set(identity.key, existing);
    });
  });

  (sales || []).forEach((sale) => {
    (sale.line_items || []).forEach((lineItem) => {
      const quantity = Number(lineItem.quantity) || 0;
      if (quantity <= 0) return;
      const matchedForecastOption = [...optionsByKey.values()].some(
        (option) => option.hasForecast && forecastItemMatchesOption(lineItem.name, option)
      );
      if (matchedForecastOption) return;

      const identity = getForecastItemOptionIdentity(lineItem);
      if (!identity.key) return;
      const existing = optionsByKey.get(identity.key) || {
        ...identity,
        forecastQuantity: 0,
        actualQuantity: 0,
        hasForecast: false,
      };
      existing.actualQuantity += quantity;
      optionsByKey.set(identity.key, existing);
    });
  });

  return [...optionsByKey.values()].sort((a, b) => {
    if (a.hasForecast !== b.hasForecast) return a.hasForecast ? -1 : 1;
    if (b.forecastQuantity !== a.forecastQuantity) return b.forecastQuantity - a.forecastQuantity;
    return a.name.localeCompare(b.name);
  });
}

function buildForecastItemChartData({ forecasts, sales, selectedOption }) {
  if (!selectedOption) return [];
  const rowsByDate = new Map();

  function ensureRow(date) {
    if (!date) return null;
    if (!rowsByDate.has(date)) {
      rowsByDate.set(date, {
        date,
        dayLabel: format(new Date(`${date}T00:00:00`), "dd.MM"),
        actualQuantity: null,
        forecastQuantity: null,
        forecastLow: null,
        forecastBand: null,
      });
    }
    return rowsByDate.get(date);
  }

  (sales || []).forEach((sale) => {
    const row = ensureRow(sale.date);
    if (!row) return;
    (sale.line_items || []).forEach((lineItem) => {
      if (!forecastItemMatchesOption(lineItem.name, selectedOption)) return;
      const quantity = Number(lineItem.quantity) || 0;
      if (quantity <= 0) return;
      row.actualQuantity = (row.actualQuantity || 0) + quantity;
    });
  });

  (forecasts || []).forEach((day) => {
    if (day.is_closed) return;
    const row = ensureRow(day.date);
    if (!row) return;
    let quantity = 0;
    let quantityLow = 0;
    let quantityHigh = 0;
    (day.items || []).forEach((item) => {
      if (!forecastItemMatchesOption(item.name, selectedOption)) return;
      quantity += Number(item.quantity) || 0;
      quantityLow += Number(item.quantity_low) || 0;
      quantityHigh += Number(item.quantity_high) || 0;
    });
    if (quantity > 0) {
      row.forecastQuantity = quantity;
      row.forecastLow = quantityLow > 0 ? quantityLow : null;
      row.forecastBand = quantityHigh > quantityLow ? quantityHigh - quantityLow : null;
    }
  });

  return [...rowsByDate.values()]
    .filter((row) => row.actualQuantity !== null || row.forecastQuantity !== null)
    .sort((a, b) => a.date.localeCompare(b.date));
}

function ProductForecastChartTooltip({ active, payload, label, t }) {
  if (!active || !payload?.length) return null;
  const point = payload.find((entry) => entry?.payload)?.payload;
  if (!point) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
      <div className="mb-2 font-medium text-slate-900">{label}</div>
      {point.actualQuantity !== null && (
        <div className="flex items-center justify-between gap-6 text-slate-600">
          <span>{t('stock.itemForecastActual')}</span>
          <span className="font-semibold text-slate-900">{formatForecastQuantity(point.actualQuantity)}</span>
        </div>
      )}
      {point.forecastQuantity !== null && (
        <div className="flex items-center justify-between gap-6 text-slate-600">
          <span>{t('stock.itemForecastForecast')}</span>
          <span className="font-semibold text-blue-700">{formatForecastQuantity(point.forecastQuantity)}</span>
        </div>
      )}
    </div>
  );
}

function ProductForecastChart({ options, selectedKey, onSelectedKeyChange, chartData, t }) {
  const selectedOption = options.find((option) => option.key === selectedKey);

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t('stock.itemForecastChartTitle')}</h2>
            <p className="text-sm text-slate-500">{t('stock.itemForecastChartSubtitle')}</p>
          </div>
          <Select value={selectedKey || ''} onValueChange={onSelectedKeyChange}>
            <SelectTrigger className="w-full bg-white md:w-72" disabled={options.length === 0}>
              <SelectValue placeholder={t('stock.itemForecastSelectItem')}>
                {selectedOption?.name}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mb-3 flex items-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-5 rounded-full bg-slate-700" />
            {t('stock.itemForecastActual')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0 w-5 border-t-2 border-dashed border-blue-600" />
            {t('stock.itemForecastForecast')}
          </span>
        </div>

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="stockItemActualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#334155" stopOpacity={0.14} />
                  <stop offset="95%" stopColor="#334155" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="stockItemForecastBandGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.14} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="dayLabel" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(value) => formatForecastQuantity(value)} />
              <Tooltip content={(props) => <ProductForecastChartTooltip {...props} t={t} />} />
              <Area
                type="monotone"
                dataKey="actualQuantity"
                stroke="#334155"
                strokeWidth={2}
                fill="url(#stockItemActualGrad)"
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Area
                type="monotone"
                dataKey="forecastLow"
                stackId="forecastRange"
                stroke="none"
                fill="none"
                connectNulls
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="forecastBand"
                stackId="forecastRange"
                stroke="none"
                fill="url(#stockItemForecastBandGrad)"
                connectNulls
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="forecastQuantity"
                stroke="#2563eb"
                strokeWidth={2}
                fill="none"
                strokeDasharray="6 4"
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[340px] items-center justify-center rounded-md border border-dashed border-slate-200 text-sm text-slate-500">
            {t('stock.itemForecastNoChartData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductForecastPanel({ forecastResponse, t }) {
  const forecasts = Array.isArray(forecastResponse?.forecasts)
    ? forecastResponse.forecasts
    : [];
  const meta = forecastResponse?.meta || {};
  const [forecastDayOffset, setForecastDayOffset] = useState(0);
  const dayWindowSize = 3;
  const forecastDays = forecasts
    .filter((day) => !day?.is_closed && Array.isArray(day.items) && day.items.length > 0);
  const maxDayOffset = Math.max(0, forecastDays.length - dayWindowSize);
  const activeDayOffset = Math.min(forecastDayOffset, maxDayOffset);
  const visibleDays = forecastDays.slice(activeDayOffset, activeDayOffset + dayWindowSize);
  const canPageBack = activeDayOffset > 0;
  const canPageForward = activeDayOffset < maxDayOffset;

  return (
    <Card className="mb-4 border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">{t('stock.itemForecastTitle')}</h2>
              <p className="text-sm text-slate-500">{t('stock.itemForecastSubtitle')}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
                {t('stock.itemForecastHistoryDays', { count: meta.history_days || 0 })}
              </Badge>
              <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
                {t('stock.itemForecastMatched', { count: meta.matched_line_items || 0 })}
              </Badge>
              {Number(meta.unmatched_line_items || 0) > 0 && (
                <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                  {t('stock.itemForecastUnmatched', { count: meta.unmatched_line_items })}
                </Badge>
              )}
            </div>
            {forecastDays.length > dayWindowSize && (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!canPageBack}
                  title={t('stock.itemForecastPreviousDays')}
                  aria-label={t('stock.itemForecastPreviousDays')}
                  onClick={() => setForecastDayOffset((offset) => Math.max(0, offset - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={!canPageForward}
                  title={t('stock.itemForecastNextDays')}
                  aria-label={t('stock.itemForecastNextDays')}
                  onClick={() => setForecastDayOffset((offset) => Math.min(maxDayOffset, offset + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {visibleDays.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500">
            {t('stock.itemForecastEmpty')}
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            {visibleDays.map((day) => {
              const topItems = day.items.slice(0, 5);
              return (
                <div key={day.date} className="rounded-md border border-slate-200 bg-white overflow-hidden">
                  <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{getForecastDayLabel(day, t)}</div>
                      <div className="text-xs text-slate-500">{day.date}</div>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <div className="font-semibold text-slate-900">EUR {formatInventoryNumber(day.revenue || 0)}</div>
                      <div>{formatForecastQuantity(day.total_quantity)} {t('stock.itemForecastUnits')}</div>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {topItems.map((item) => (
                      <div key={`${day.date}-${item.name}`} className="px-3 py-2 grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{item.name}</div>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            <Badge className={`${getForecastConfidenceClass(item.confidence)} text-[11px]`}>
                              {getForecastConfidenceLabel(item.confidence, t)}
                            </Badge>
                            {item.source !== 'recipe' && (
                              <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[11px]">
                                <AlertTriangle className="mr-1 h-3 w-3" />
                                {t('stock.itemForecastNoRecipe')}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs whitespace-nowrap">
                          <div className="text-sm font-semibold text-slate-900">
                            {formatForecastQuantity(item.quantity)}
                          </div>
                          <div className="text-slate-500">
                            {formatForecastQuantity(item.quantity_low)}-{formatForecastQuantity(item.quantity_high)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 py-2 bg-slate-50 text-xs text-slate-500 flex items-center justify-between">
                    <span>{t('stock.itemForecastEffort')}</span>
                    <span className="font-semibold text-slate-700">
                      {formatForecastQuantity(day.effort_minutes)} min
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getExpectedDeliveryDate(orderDate, leadTimeDays) {
  if (!orderDate || leadTimeDays == null || leadTimeDays === '') return '';
  const numericLeadTime = Number(leadTimeDays);
  if (!Number.isFinite(numericLeadTime) || numericLeadTime < 0) return '';
  return format(addDays(new Date(orderDate), numericLeadTime), 'yyyy-MM-dd');
}

function isCriticalStock(item) {
  return Number(item.minimum_quantity) > 0 && Number(item.current_quantity) <= Number(item.minimum_quantity);
}

function isLowStock(item) {
  return isBelowReorderPoint(item);
}

function getProjectedStockSeverity(item) {
  const daysUntil = item.days_until_reorder_breach;
  if (daysUntil === null || daysUntil === undefined || daysUntil <= 2) {
    return {
      container: 'bg-red-50 border-red-100',
      icon: 'text-red-500',
      label: 'bg-red-100 text-red-700 border-red-200',
      value: item.stock_after < 0 ? 'text-red-700' : 'text-red-600',
    };
  }
  if (daysUntil <= 5) {
    return {
      container: 'bg-amber-50 border-amber-100',
      icon: 'text-amber-500',
      label: 'bg-amber-100 text-amber-700 border-amber-200',
      value: 'text-amber-600',
    };
  }
  return {
    container: 'bg-white border-yellow-100',
    icon: 'text-yellow-500',
    label: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    value: 'text-yellow-700',
  };
}

function getStockSortScore(item) {
  if (isCriticalStock(item)) return 0;
  if (isLowStock(item)) return 1;
  return 2;
}

function getNextSortConfig(currentConfig, key) {
  if (currentConfig.key === key) {
    return { key, direction: currentConfig.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: 'asc' };
}

function compareSortValues(a, b, direction) {
  const firstEmpty = a == null || a === '';
  const secondEmpty = b == null || b === '';

  if (firstEmpty && secondEmpty) return 0;
  if (firstEmpty) return 1;
  if (secondEmpty) return -1;

  let result = 0;

  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  return direction === 'asc' ? result : -result;
}

function sortRows(rows, sortConfig, getSortValue) {
  if (!sortConfig.key) return rows;

  return [...rows].sort((a, b) =>
    compareSortValues(
      getSortValue(a, sortConfig.key),
      getSortValue(b, sortConfig.key),
      sortConfig.direction,
    )
  );
}

function SortableHeader({ label, sortKey, sortConfig, onSort, className = '', align = 'left' }) {
  const isActive = sortConfig.key === sortKey;
  const buttonAlignment = align === 'right' ? 'justify-end ml-auto' : 'justify-start';

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group flex w-full items-center gap-1 text-left text-xs font-medium text-slate-600 transition-colors hover:text-blue-600 ${buttonAlignment}`}
      >
        <span>{label}</span>
        {isActive ? (
          sortConfig.direction === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5 text-blue-600" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-blue-600" />
          )
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </button>
    </TableHead>
  );
}

export default function Stock() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // ─── Tab state from URL ───
  const initialTab = searchParams.get('tab') || 'products';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedForecastItemKey, setSelectedForecastItemKey] = useState('');
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'products' ? {} : { tab });
  };

  // ─── Inventory dialog state ───
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [newItem, setNewItem] = useState(normalizeInventoryItemForm());
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deleteSupplierId, setDeleteSupplierId] = useState(null);
  const [newSupplier, setNewSupplier] = useState({ name: '', lead_time_days: '' });
  const inventoryUnitOptions = useMemo(() => {
    const activeUnit = normalizeInventoryUnit(newItem.unit);
    if (!activeUnit || CANONICAL_INVENTORY_UNIT_OPTIONS.includes(activeUnit)) {
      return CANONICAL_INVENTORY_UNIT_OPTIONS;
    }
    return [activeUnit, ...CANONICAL_INVENTORY_UNIT_OPTIONS];
  }, [newItem.unit]);

  // ─── Loss dialog state ───
  const [isLossDialogOpen, setIsLossDialogOpen] = useState(false);
  const [newLoss, setNewLoss] = useState({
    inventory_item_name: '', quantity: '', unit: 'kg',
    date: format(new Date(), 'yyyy-MM-dd'), reason: 'Produktionsfehler',
    description: '', cost: '', shift: 'Abend', reported_by: '',
  });

  // ─── New order dialog state ───
  const [newOrderDialog, setNewOrderDialog] = useState(false);
  const [orderForm, setOrderForm] = useState(EMPTY_ORDER_FORM);

  // ─── Orders filter state ───
  const [ordersFilter, setOrdersFilter] = useState('active');
  const [productsSort, setProductsSort] = useState({ key: null, direction: 'asc' });
  const [shoppingCartSort, setShoppingCartSort] = useState({ key: null, direction: 'asc' });
  const [ordersTableSort, setOrdersTableSort] = useState({ key: null, direction: 'asc' });
  const [suppliersSort, setSuppliersSort] = useState({ key: null, direction: 'asc' });
  const [lossesSort, setLossesSort] = useState({ key: null, direction: 'asc' });

  const queryClient = useQueryClient();

  // ────────────────────────────────────────────
  // QUERIES
  // ────────────────────────────────────────────
  const { data: inventory, isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => base44.entities.InventoryItem.list(),
    initialData: [],
  });

  const { data: losses, isLoading: loadingLosses } = useQuery({
    queryKey: ['productionlosses'],
    queryFn: () => base44.entities.ProductionLoss.list('-date'),
    initialData: [],
  });

  const { data: orders, isLoading: loadingOrders } = useQuery({
    queryKey: ['orders'],
    queryFn: () => base44.entities.Order.list('-order_date'),
    initialData: [],
  });

  const { data: suppliers = [], isLoading: loadingSuppliers } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => base44.entities.Supplier.list('name'),
    initialData: [],
  });

  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
  });

  const { data: sales = [] } = useQuery({
    queryKey: ['sales'],
    queryFn: () => base44.entities.Sale.list('-date'),
    initialData: [],
  });

  const { data: forecastResponse } = useQuery({
    queryKey: ['forecast', 'products', FORECAST_HORIZON_DAYS],
    queryFn: () => base44.forecast.getProducts(FORECAST_HORIZON_DAYS),
    placeholderData: { forecasts: [] },
    staleTime: 5 * 60 * 1000,
  });

  // Projected stock depletion over the product forecast horizon based on recipe quantities.
  const stockAlerts = useMemo(() => {
    return calculateProjectedStockAlerts({
      forecasts: forecastResponse?.forecasts || [],
      recipes,
      inventory,
    });
  }, [forecastResponse, recipes, inventory]);

  const upcomingStockAlerts = useMemo(() =>
    stockAlerts.filter((item) => !isLowStock(item)),
    [stockAlerts]
  );

  const forecastItemOptions = useMemo(() =>
    buildForecastItemOptions(forecastResponse?.forecasts || [], sales),
    [forecastResponse, sales]
  );

  const effectiveForecastItemKey = useMemo(() => {
    if (forecastItemOptions.some((option) => option.key === selectedForecastItemKey)) {
      return selectedForecastItemKey;
    }
    return forecastItemOptions[0]?.key || '';
  }, [forecastItemOptions, selectedForecastItemKey]);

  const selectedForecastItem = useMemo(() =>
    forecastItemOptions.find((option) => option.key === effectiveForecastItemKey) || null,
    [forecastItemOptions, effectiveForecastItemKey]
  );

  const forecastItemChartData = useMemo(() =>
    buildForecastItemChartData({
      forecasts: forecastResponse?.forecasts || [],
      sales,
      selectedOption: selectedForecastItem,
    }),
    [forecastResponse, sales, selectedForecastItem]
  );

  // ────────────────────────────────────────────
  // MUTATIONS
  // ────────────────────────────────────────────
  const createItemMutation = useMutation({
    mutationFn: (d) => base44.entities.InventoryItem.create(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory'] }); setIsDialogOpen(false); setEditingItem(null); resetForm(); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.InventoryItem.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory'] }); setIsDialogOpen(false); setEditingItem(null); resetForm(); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id) => base44.entities.InventoryItem.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory'] }); setDeleteItemId(null); },
    onError: (e) => { alert(e?.response?.data?.detail || e.message || 'Delete failed'); setDeleteItemId(null); },
  });

  const createLossMutation = useMutation({
    mutationFn: (d) => base44.entities.ProductionLoss.create(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productionlosses'] });
      setIsLossDialogOpen(false);
      setNewLoss({ inventory_item_name: '', quantity: '', unit: 'kg', date: format(new Date(), 'yyyy-MM-dd'), reason: 'Produktionsfehler', description: '', cost: '', shift: 'Abend', reported_by: '' });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const createSingleOrderMutation = useMutation({
    mutationFn: (d) => base44.entities.Order.create(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['orders'] }); setNewOrderDialog(false); setOrderForm(EMPTY_ORDER_FORM); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const syncOrderListMutation = useMutation({
    mutationFn: (entries) => Promise.all(entries.map(({ existingOrder, data }) => {
      if (existingOrder?.id) {
        return base44.entities.Order.update(existingOrder.id, data);
      }
      return base44.entities.Order.create(data);
    })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const markOrderListOrderedMutation = useMutation({
    mutationFn: (group) => {
      const today = format(new Date(), 'yyyy-MM-dd');
      return Promise.all(group.items.map((order) => {
        const selectedSupplier = suppliers.find((supplier) =>
          String(supplier.id) === String(order.supplier_id) ||
          supplier.name === (order.supplier_name || order.supplier)
        );
        const expectedDeliveryDate =
          getExpectedDeliveryDate(today, selectedSupplier?.lead_time_days ?? 2) ||
          order.expected_delivery_date ||
          null;
        return base44.entities.Order.update(order.id, {
          status: t("orders.statusOrdered"),
          order_date: today,
          expected_delivery_date: expectedDeliveryDate,
        });
      }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const updateOrderMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Order.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['orders'] }); queryClient.invalidateQueries({ queryKey: ['inventory'] }); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const createSupplierMutation = useMutation({
    mutationFn: (data) => base44.entities.Supplier.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setIsSupplierDialogOpen(false);
      setEditingSupplier(null);
      setNewSupplier({ name: '', lead_time_days: '' });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const updateSupplierMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Supplier.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setIsSupplierDialogOpen(false);
      setEditingSupplier(null);
      setNewSupplier({ name: '', lead_time_days: '' });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: (id) => base44.entities.Supplier.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setDeleteSupplierId(null);
    },
    onError: (e) => {
      alert(e?.response?.data?.detail || e.message || 'Delete failed');
      setDeleteSupplierId(null);
    },
  });

  // ────────────────────────────────────────────
  // DERIVED DATA
  // ────────────────────────────────────────────
  const lowStockItems = useMemo(() =>
    inventory
      .filter(isLowStock)
      .sort((a, b) => getReorderRatio(a) - getReorderRatio(b)),
    [inventory]
  );

  const criticalItems = useMemo(() =>
    inventory.filter(isCriticalStock),
    [inventory]
  );

  const hasCritical = criticalItems.length > 0;

  const suggestionOrders = useMemo(() =>
    orders.filter(o => o.status === t("orders.statusSuggestion")),
    [orders, t]
  );

  const suggestionByInventoryItemId = useMemo(() => {
    const suggestions = new Map();
    suggestionOrders.forEach((order) => {
      if (order.inventory_item_id) {
        suggestions.set(String(order.inventory_item_id), order);
      }
    });
    return suggestions;
  }, [suggestionOrders]);

  const orderListGroups = useMemo(() =>
    groupOrdersBySupplier(suggestionOrders, t('suppliers.noSupplier')),
    [suggestionOrders, t]
  );

  // Orders tab filtering
  const filteredOrders = useMemo(() => {
    if (ordersFilter === 'active') {
      return orders.filter(o =>
        o.status === t("orders.statusOrdered") || o.status === t("orders.statusArriving")
      );
    }
    if (ordersFilter === 'suggestions') {
      return suggestionOrders;
    }
    // history
    return orders.filter(o =>
      o.status === t("orders.statusDelivered") || o.status === t("orders.statusCancelled")
    );
  }, [orders, ordersFilter, suggestionOrders, t]);

  // Products tab: sort critical/low to top
  const filteredInventory = useMemo(() => {
    const getCatValue = (translatedCategory) => {
      const map = {
        [t('inventory.freshIngredients')]: 'Frische Zutaten',
        [t('inventory.dryGoods')]: 'Trockenwaren',
        [t('inventory.beverages')]: 'Getränke',
        [t('inventory.packaging')]: 'Verpackung',
        [t('inventory.other')]: 'Sonstiges',
      };
      return map[translatedCategory] || translatedCategory;
    };
    let items;
    if (filterCategory === 'all') {
      items = [...inventory];
    } else if (filterCategory === '__missing_cost__') {
      items = inventory.filter(isMissingCost);
    } else {
      items = inventory.filter(item => item.category === getCatValue(filterCategory));
    }
    const q = productSearch.trim().toLowerCase();
    if (q) {
      items = items.filter((item) => {
        const name = (item.name || '').toLowerCase();
        const article = (item.article_number || '').toLowerCase();
        const supplier = (item.supplier_name || item.supplier || '').toLowerCase();
        return name.includes(q) || article.includes(q) || supplier.includes(q);
      });
    }
    // Sort: critical first, then low, then OK
    items.sort((a, b) => {
      return getStockSortScore(a) - getStockSortScore(b);
    });
    return items;
  }, [inventory, filterCategory, productSearch, t]);

  const sortedInventory = useMemo(() =>
    sortRows(filteredInventory, productsSort, (item, key) => {
      switch (key) {
        case 'product':
          return item.name;
        case 'stock':
          return getReorderRatio(item);
        case 'category':
          return t(`inventory.categories.${item.category}`, item.category);
        case 'supplier':
          return item.supplier_name || item.supplier || '';
        case 'cost':
          return getDerivedCostPerUnit(item);
        default:
          return '';
      }
    }),
  [filteredInventory, productsSort, t]);

  const supplierStats = useMemo(() => {
    return suppliers.map((supplier) => ({
      ...supplier,
      productCount: inventory.filter(item => String(item.supplier_id) === String(supplier.id)).length,
      activeOrderCount: orders.filter(order =>
        String(order.supplier_id) === String(supplier.id) &&
        (order.status === t("orders.statusOrdered") || order.status === t("orders.statusArriving"))
      ).length,
    }));
  }, [inventory, orders, suppliers, t]);

  const sortedSupplierStats = useMemo(() =>
    sortRows(supplierStats, suppliersSort, (supplier, key) => {
      switch (key) {
        case 'name':
          return supplier.name;
        case 'leadTime':
          return supplier.lead_time_days;
        case 'products':
          return supplier.productCount;
        case 'activeOrders':
          return supplier.activeOrderCount;
        default:
          return '';
      }
    }),
  [supplierStats, suppliersSort]);

  const sortedOrderListGroups = useMemo(() =>
    orderListGroups.map((group) => ({
      ...group,
      items: sortRows(group.items, shoppingCartSort, (order, key) => {
        switch (key) {
          case 'product':
            return order.inventory_item_name;
          case 'articleNumber':
            return order.article_number;
          case 'quantity':
            return Number(order.suggested_quantity) || 0;
          case 'delivery':
            return order.expected_delivery_date ? new Date(order.expected_delivery_date).getTime() : null;
          case 'cost':
            return Number(order.estimated_cost) || 0;
          default:
            return '';
        }
      }),
    })),
  [orderListGroups, shoppingCartSort]);

  const sortedFilteredOrders = useMemo(() =>
    sortRows(filteredOrders, ordersTableSort, (order, key) => {
      switch (key) {
        case 'product':
          return order.inventory_item_name;
        case 'articleNumber':
          return order.article_number;
        case 'quantity':
          return Number(order.suggested_quantity) || 0;
        case 'supplier':
          return order.supplier_name || order.supplier || '';
        case 'orderedOn':
          return order.order_date ? new Date(order.order_date).getTime() : null;
        case 'delivery':
          return order.expected_delivery_date ? new Date(order.expected_delivery_date).getTime() : null;
        case 'status':
          return order.status;
        default:
          return '';
      }
    }),
  [filteredOrders, ordersTableSort]);

  const reasonColors = {
    'Kaputt': 'bg-red-100 text-red-700', 'Verdorben': 'bg-orange-100 text-orange-700',
    'Produktionsfehler': 'bg-yellow-100 text-yellow-700', 'Verschüttet': 'bg-blue-100 text-blue-700',
    'Verbrannt': 'bg-purple-100 text-purple-700', 'Abgelaufen': 'bg-pink-100 text-pink-700',
    'Qualitätsmangel': 'bg-indigo-100 text-indigo-700', 'Sonstiges': 'bg-slate-100 text-slate-700',
  };

  const reasonLabels = useMemo(() => ({
      'Kaputt': t('inventory.broken'), 'Verdorben': t('inventory.spoiled'),
      'Produktionsfehler': t('inventory.productionError'), 'Verschüttet': t('inventory.spilled'),
      'Verbrannt': t('inventory.burned'), 'Abgelaufen': t('inventory.expired'),
      'Qualitätsmangel': t('inventory.qualityIssue'), 'Sonstiges': t('inventory.otherReason'),
    }),
  [t]);

  // Losses weekly summary
  const weekLossSummary = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const weekLosses = losses.filter(l => {
      try {
        const d = new Date(l.date);
        return d >= weekStart && d <= weekEnd;
      } catch { return false; }
    });
    return {
      count: weekLosses.length,
      cost: weekLosses.reduce((s, l) => s + (l.cost || 0), 0),
    };
  }, [losses]);

  const sortedLosses = useMemo(() =>
    sortRows(losses, lossesSort, (loss, key) => {
      switch (key) {
        case 'date':
          return loss.date ? new Date(loss.date).getTime() : null;
        case 'product':
          return loss.inventory_item_name;
        case 'quantity':
          return Number(loss.quantity) || 0;
        case 'reason':
          return reasonLabels[loss.reason] || loss.reason;
        case 'shift':
          return loss.shift === 'Mittag' ? t('inventory.lunch') : loss.shift === 'Abend' ? t('inventory.dinner') : loss.shift;
        case 'cost':
          return Number(loss.cost) || 0;
        case 'reportedBy':
          return loss.reported_by;
        default:
          return '';
      }
    }),
  [losses, lossesSort, reasonLabels, t]);

  // ────────────────────────────────────────────
  // HANDLERS
  // ────────────────────────────────────────────
  const resetForm = () => {
    setNewItem(normalizeInventoryItemForm());
  };

  const resetSupplierForm = () => {
    setNewSupplier({ name: '', lead_time_days: '' });
  };

  const openSupplierManager = () => {
    setIsDialogOpen(false);
    setNewOrderDialog(false);
    handleTabChange('suppliers');
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setNewSupplier({
      name: supplier.name || '',
      lead_time_days: supplier.lead_time_days != null ? String(supplier.lead_time_days) : '',
    });
    setIsSupplierDialogOpen(true);
  };

  const handleSupplierSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: newSupplier.name.trim(),
      lead_time_days: newSupplier.lead_time_days !== '' ? Number(newSupplier.lead_time_days) : null,
    };
    if (editingSupplier) {
      updateSupplierMutation.mutate({ id: editingSupplier.id, data: payload });
      return;
    }
    createSupplierMutation.mutate(payload);
  };

  const handleItemSupplierSelect = (value) => {
    if (value === 'none') {
      setNewItem((item) => ({ ...item, supplier_id: '', supplier: '' }));
      return;
    }
    const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(value));
    if (!selectedSupplier) return;
    setNewItem((item) => ({
      ...item,
      supplier_id: String(selectedSupplier.id),
      supplier: selectedSupplier.name,
      lead_time_days:
        selectedSupplier.lead_time_days != null
          ? String(selectedSupplier.lead_time_days)
          : item.lead_time_days,
    }));
  };

  const handleOrderSupplierSelect = (value) => {
    if (value === 'none') {
      setOrderForm((form) => ({ ...form, supplier_id: '', supplier: '' }));
      return;
    }
    const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(value));
    if (!selectedSupplier) return;
    setOrderForm((form) => ({
      ...form,
      supplier_id: String(selectedSupplier.id),
      supplier: selectedSupplier.name,
      expected_delivery_date:
        getExpectedDeliveryDate(form.order_date, selectedSupplier.lead_time_days) || form.expected_delivery_date,
    }));
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const matchedSupplier = suppliers.find((supplier) => supplier.name === (item.supplier_name || item.supplier));
    setNewItem(normalizeInventoryItemForm({
      ...item,
      supplier_id: item.supplier_id || matchedSupplier?.id,
    }));
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const itemData = buildInventoryItemPayload(newItem);
    if (editingItem) {
      updateItemMutation.mutate({ id: editingItem.id, data: itemData });
    } else {
      createItemMutation.mutate(itemData);
    }
  };

  const handleLossSubmit = (e) => {
    e.preventDefault();
    createLossMutation.mutate({
      ...newLoss,
      quantity: parseLocaleNumber(newLoss.quantity),
      cost: parseLocaleNumber(newLoss.cost),
    });
  };

  const buildSuggestedOrderPayload = (item, options = {}) => {
    const stockReferenceQuantity = options.stockReferenceQuantity ?? item.current_quantity;
    const deficit = item.reorder_point - stockReferenceQuantity;
    const buffer = (item.reorder_point - item.minimum_quantity) * 0.5;
    const plan = getPurchasePlan(item, Math.ceil(deficit + buffer));
    const today = new Date();
    const expected = addDays(today, item.lead_time_days || 2);

    return {
      inventory_item_id: item.id,
      inventory_item_name: item.name,
      article_number: item.article_number || '',
      purchase_units_ordered: plan.hasPurchaseConfig ? plan.purchaseUnitsNeeded : null,
      package_label: plan.packageLabel || getPurchaseUnitLabel(item),
      suggested_quantity: plan.coverageQuantity,
      unit: item.unit,
      supplier_id: item.supplier_id || null,
      supplier: item.supplier_name || item.supplier || '',
      order_date: format(today, 'yyyy-MM-dd'),
      expected_delivery_date: format(expected, 'yyyy-MM-dd'),
      status: t("orders.statusSuggestion"),
      estimated_cost: Number(plan.estimatedCost.toFixed(2)),
      notes: plan.hasPurchaseConfig
        ? `${t("orders.automaticallyGenerated", { current: stockReferenceQuantity, unit: item.unit, reorder: item.reorder_point })} ${plan.summary}`
        : t("orders.automaticallyGenerated", { current: stockReferenceQuantity, unit: item.unit, reorder: item.reorder_point }),
    };
  };

  // ─── Add/update one item in the order list from Attention Strip ───
  const handleQuickOrder = (item) => {
    syncOrderListMutation.mutate([{
      existingOrder: suggestionByInventoryItemId.get(String(item.id)),
      data: buildSuggestedOrderPayload(item),
    }]);
  };

  // ─── Add/update all low-stock items in the order list ───
  const handleOrderAll = () => {
    const entries = lowStockItems.map(item => ({
      existingOrder: suggestionByInventoryItemId.get(String(item.id)),
      data: buildSuggestedOrderPayload(item),
    }));
    if (entries.length > 0) {
      syncOrderListMutation.mutate(entries);
    }
  };

  const handleProjectedQuickOrder = (item) => {
    syncOrderListMutation.mutate([{
      existingOrder: suggestionByInventoryItemId.get(String(item.id)),
      data: buildSuggestedOrderPayload(item, { stockReferenceQuantity: item.stock_after }),
    }]);
  };

  const handleProjectedOrderAll = () => {
    const entries = upcomingStockAlerts.map(item => ({
      existingOrder: suggestionByInventoryItemId.get(String(item.id)),
      data: buildSuggestedOrderPayload(item, { stockReferenceQuantity: item.stock_after }),
    }));
    if (entries.length > 0) {
      syncOrderListMutation.mutate(entries);
    }
  };

  // ─── Order status actions ───
  const handleMarkOrdered = (order) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    updateOrderMutation.mutate({
      id: order.id,
      data: {
        status: t("orders.statusOrdered"),
        order_date: today,
        expected_delivery_date: order.expected_delivery_date || getExpectedDeliveryDate(today, 2) || null,
      },
    });
  };
  const handleMarkArriving = (order) => {
    updateOrderMutation.mutate({ id: order.id, data: { status: t("orders.statusArriving") } });
  };
  const handleMarkDelivered = (order) => {
    updateOrderMutation.mutate({
      id: order.id,
      data: { status: t("orders.statusDelivered"), actual_delivery_date: format(new Date(), 'yyyy-MM-dd') },
    });
  };
  const handleCancelOrder = (order) => {
    updateOrderMutation.mutate({ id: order.id, data: { status: t("orders.statusCancelled") } });
  };

  const handleDownloadOrderListPdf = (group) => {
    const generatedAt = new Date();
    const bytes = buildOrderListPdfBytes({
      supplierLabel: group.supplierLabel,
      orders: group.items,
      generatedAt,
      labels: {
        title: t('stock.orderListPdfTitle'),
        subtitle: t('stock.orderListPdfSubtitle'),
        orderDetails: t('stock.orderDetails'),
        items: t('stock.orderItems'),
        supplier: t('orders.supplier'),
        generatedAt: t('stock.generatedAt'),
        product: t('orders.product'),
        articleNumber: t('orders.articleNumber'),
        quantity: t('orders.quantity'),
        unknown: t('orders.unknown'),
      },
    });
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildOrderListPdfFilename(group.supplierLabel, generatedAt);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleProductsSort = (key) => {
    setProductsSort((current) => getNextSortConfig(current, key));
  };

  const handleShoppingCartSort = (key) => {
    setShoppingCartSort((current) => getNextSortConfig(current, key));
  };

  const handleOrdersTableSort = (key) => {
    setOrdersTableSort((current) => getNextSortConfig(current, key));
  };

  const handleSuppliersSort = (key) => {
    setSuppliersSort((current) => getNextSortConfig(current, key));
  };

  const handleLossesSort = (key) => {
    setLossesSort((current) => getNextSortConfig(current, key));
  };

  // ─── New Order dialog handlers ───
  const handleInventoryItemSelect = (itemId) => {
    if (itemId === 'manual') {
      setOrderForm(f => ({
        ...f,
        inventory_item_id: '',
        inventory_item_name: '',
        article_number: '',
        package_label: '',
        unit: '',
      }));
      return;
    }
    const item = inventory.find(i => String(i.id) === String(itemId));
    if (item) {
      const matchedSupplier = suppliers.find((supplier) => supplier.name === (item.supplier_name || item.supplier));
      const orderDraft = orderForm.suggested_quantity ? getOrderDraft(item, parseLocaleNumber(orderForm.suggested_quantity)) : null;
      setOrderForm(f => ({
        ...f,
        inventory_item_id: item.id,
        inventory_item_name: item.name,
        article_number: item.article_number || f.article_number,
        package_label: getPurchaseUnitLabel(item) || f.package_label,
        unit: item.unit,
        supplier_id: item.supplier_id ? String(item.supplier_id) : matchedSupplier ? String(matchedSupplier.id) : f.supplier_id,
        supplier: item.supplier_name || item.supplier || f.supplier,
        expected_delivery_date:
          getExpectedDeliveryDate(f.order_date, item.lead_time_days) || f.expected_delivery_date,
        estimated_cost: orderDraft ? orderDraft.estimatedCost.toFixed(2) : f.estimated_cost,
      }));
    }
  };

  const handleSubmitNewOrder = () => {
    const finalQuantity = selectedOrderDraft
      ? selectedOrderDraft.coverageQuantity
      : parseLocaleNumber(orderForm.suggested_quantity) || 0;
    const payload = {
      inventory_item_name: orderForm.inventory_item_name,
      article_number: orderForm.article_number,
      purchase_units_ordered: selectedOrderDraft?.usesPurchaseUnits
        ? Number(selectedOrderDraft.purchaseUnitsOrdered)
        : null,
      package_label: orderForm.package_label,
      suggested_quantity: finalQuantity,
      unit: orderForm.unit,
      supplier_id: orderForm.supplier_id || null,
      supplier: orderForm.supplier,
      order_date: orderForm.order_date,
      expected_delivery_date: orderForm.expected_delivery_date || null,
      estimated_cost: selectedOrderDraft ? Number(selectedOrderDraft.estimatedCost.toFixed(2)) : parseLocaleNumber(orderForm.estimated_cost) || 0,
      status: orderForm.status || t("orders.statusSuggestion"),
      notes: selectedOrderDraft?.usesPurchaseUnits && !orderForm.notes
        ? selectedOrderDraft.summary
        : orderForm.notes,
    };
    if (orderForm.inventory_item_id) payload.inventory_item_id = orderForm.inventory_item_id;
    createSingleOrderMutation.mutate(payload);
  };

  const isFormValid = orderForm.inventory_item_name && orderForm.suggested_quantity && orderForm.unit;
  const selectedOrderItem = inventory.find(i => String(i.id) === String(orderForm.inventory_item_id));
  const selectedOrderDraft = selectedOrderItem && orderForm.suggested_quantity
    ? getOrderDraft(selectedOrderItem, parseLocaleNumber(orderForm.suggested_quantity))
    : null;

  // ─── Status helpers ───
  const getStockStatus = (item) => {
    if (isCriticalStock(item)) return { label: t('inventory.critical'), color: 'bg-red-100 text-red-700 border-red-200' };
    if (isLowStock(item)) return { label: t('inventory.low'), color: 'bg-orange-100 text-orange-700 border-orange-200' };
    return { label: t('inventory.ok'), color: 'bg-green-100 text-green-700 border-green-200' };
  };

  const getStatusColor = (status) => {
    if (status === t("orders.statusSuggestion")) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (status === t("orders.statusOrdered")) return 'bg-orange-100 text-orange-700 border-orange-200';
    if (status === t("orders.statusArriving")) return 'bg-teal-100 text-teal-700 border-teal-200';
    if (status === t("orders.statusDelivered")) return 'bg-green-100 text-green-700 border-green-200';
    if (status === t("orders.statusCancelled")) return 'bg-slate-100 text-slate-700 border-slate-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd.MM.yyyy'); } catch { return '-'; }
  };

  const renderHeaderAction = () => {
    if (activeTab === 'losses') {
      return (
        <Dialog open={isLossDialogOpen} onOpenChange={setIsLossDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="shadow-md border-red-200 text-red-700 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" />
              {t('inventory.reportLoss')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                {t('inventory.reportProductionLoss')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleLossSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="loss_item">{t('inventory.lossProduct')}</Label>
                  <Select value={newLoss.inventory_item_name} onValueChange={(value) => {
                    const item = inventory.find(i => i.name === value);
                    setNewLoss({ ...newLoss, inventory_item_name: value, inventory_item_id: item?.id, unit: item?.unit || 'kg', cost: item?.cost_per_unit ? (parseLocaleNumber(newLoss.quantity || 0) * item.cost_per_unit).toFixed(2) : '' });
                  }}>
                    <SelectTrigger><SelectValue placeholder={t('inventory.selectProduct')} /></SelectTrigger>
                    <SelectContent>
                      {inventory.map(item => (<SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="loss_reason">{t('inventory.reason')}</Label>
                  <Select value={newLoss.reason} onValueChange={(v) => setNewLoss({ ...newLoss, reason: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Kaputt">{t('inventory.broken')}</SelectItem>
                      <SelectItem value="Verdorben">{t('inventory.spoiled')}</SelectItem>
                      <SelectItem value="Produktionsfehler">{t('inventory.productionError')}</SelectItem>
                      <SelectItem value="Verschüttet">{t('inventory.spilled')}</SelectItem>
                      <SelectItem value="Verbrannt">{t('inventory.burned')}</SelectItem>
                      <SelectItem value="Abgelaufen">{t('inventory.expired')}</SelectItem>
                      <SelectItem value="Qualitätsmangel">{t('inventory.qualityIssue')}</SelectItem>
                      <SelectItem value="Sonstiges">{t('inventory.otherReason')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="loss_quantity">{t('inventory.quantity')}</Label>
                  <Input id="loss_quantity" type="number" step="0.001" value={newLoss.quantity} onChange={(e) => {
                    const item = inventory.find(i => i.name === newLoss.inventory_item_name);
                    setNewLoss({ ...newLoss, quantity: e.target.value, cost: item?.cost_per_unit ? (parseLocaleNumber(e.target.value || 0) * item.cost_per_unit).toFixed(2) : '' });
                  }} required />
                </div>
                <div>
                  <Label htmlFor="loss_unit">{t('inventory.unit')}</Label>
                  <Input id="loss_unit" value={newLoss.unit} disabled />
                </div>
                <div>
                  <Label htmlFor="loss_date">{t('inventory.date')}</Label>
                  <Input id="loss_date" type="date" value={newLoss.date} onChange={(e) => setNewLoss({ ...newLoss, date: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="loss_shift">{t('inventory.shift')}</Label>
                  <Select value={newLoss.shift} onValueChange={(v) => setNewLoss({ ...newLoss, shift: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mittag">{t('inventory.lunch')}</SelectItem>
                      <SelectItem value="Abend">{t('inventory.dinner')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="loss_cost">{t('inventory.cost')}</Label>
                  <Input id="loss_cost" type="number" step="0.01" value={newLoss.cost} onChange={(e) => setNewLoss({ ...newLoss, cost: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="loss_reported">{t('inventory.reportedBy')}</Label>
                  <Input id="loss_reported" value={newLoss.reported_by} onChange={(e) => setNewLoss({ ...newLoss, reported_by: e.target.value })} placeholder={t('inventory.name')} />
                </div>
              </div>
              <div>
                <Label htmlFor="loss_description">{t('inventory.description')}</Label>
                <Textarea id="loss_description" value={newLoss.description} onChange={(e) => setNewLoss({ ...newLoss, description: e.target.value })} placeholder={t('inventory.whatHappened')} rows={3} />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsLossDialogOpen(false)}>{t('inventory.cancel')}</Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700">{t('inventory.reportLoss')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      );
    }

    if (activeTab === 'products') {
      return (
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { setEditingItem(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="shadow-md">
              <Plus className="w-4 h-4 mr-2" />
              {t('inventory.addProduct')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? t('inventory.editProduct') : t('inventory.newProduct')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t('inventory.productName')}</Label>
                  <Input id="name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="article_number">{t('inventory.articleNumber')}</Label>
                  <Input
                    id="article_number"
                    value={newItem.article_number}
                    onChange={(e) => setNewItem({ ...newItem, article_number: e.target.value })}
                    placeholder={t('inventory.articleNumberPlaceholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="category">{t('inventory.category')}</Label>
                  <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Frische Zutaten">{t('inventory.freshIngredients')}</SelectItem>
                      <SelectItem value="Trockenwaren">{t('inventory.dryGoods')}</SelectItem>
                      <SelectItem value="Getränke">{t('inventory.beverages')}</SelectItem>
                      <SelectItem value="Verpackung">{t('inventory.packaging')}</SelectItem>
                      <SelectItem value="Sonstiges">{t('inventory.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="current_quantity">{t('inventory.currentStock')}</Label>
                  <Input id="current_quantity" type="number" step="0.001" value={newItem.current_quantity} onChange={(e) => setNewItem({ ...newItem, current_quantity: e.target.value })} required />
                </div>
                <div aria-hidden="true" className="md:col-span-2 my-1 border-t border-slate-200" />
                <div>
                  <Label htmlFor="unit">{t('inventory.unit')}</Label>
                  <Select value={newItem.unit} onValueChange={(v) => setNewItem({ ...newItem, unit: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {inventoryUnitOptions.map((unitOption) => (
                        <SelectItem key={unitOption} value={unitOption}>{unitOption}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="purchase_unit_quantity">{t('inventory.purchaseUnitSize')}</Label>
                  <Input
                    id="purchase_unit_quantity"
                    type="number"
                    min="0"
                    step="0.001"
                    value={newItem.purchase_unit_quantity}
                    onChange={(e) => setNewItem({ ...newItem, purchase_unit_quantity: e.target.value })}
                    placeholder={t('inventory.purchaseUnitSizePlaceholder')}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {t('inventory.purchaseUnitSizeDesc', {
                      size: newItem.purchase_unit_quantity || "1",
                      unit: newItem.unit || "kg",
                    })}
                  </p>
                </div>
                <div>
                  <Label htmlFor="purchase_unit_name">{t('inventory.packageType')}</Label>
                  <Input
                    id="purchase_unit_name"
                    value={newItem.purchase_unit_name}
                    onChange={(e) => setNewItem({ ...newItem, purchase_unit_name: e.target.value })}
                    placeholder={t('inventory.packageTypePlaceholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="purchase_unit_cost">{t('inventory.purchaseUnitCost')}</Label>
                  <Input
                    id="purchase_unit_cost"
                    type="number"
                    min="0"
                    step="0.001"
                    value={newItem.purchase_unit_cost}
                    onChange={(e) => setNewItem({ ...newItem, purchase_unit_cost: e.target.value })}
                    placeholder="15.74"
                  />
                  {getDerivedCostPerUnit(newItem) !== null && (
                    <p className="text-xs text-slate-500 mt-1">
                      {t('inventory.recipeCostHint', {
                        cost: formatInventoryNumber(getDerivedCostPerUnit(newItem)),
                        unit: newItem.unit || "kg",
                      })}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="minimum_quantity">{t('inventory.minimumQuantity')}</Label>
                  <Input id="minimum_quantity" type="number" step="0.001" value={newItem.minimum_quantity} onChange={(e) => setNewItem({ ...newItem, minimum_quantity: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="reorder_point">{t('inventory.reorderPoint')}</Label>
                  <Input id="reorder_point" type="number" step="0.001" value={newItem.reorder_point} onChange={(e) => setNewItem({ ...newItem, reorder_point: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="supplier">{t('inventory.supplier')}</Label>
                  <Select value={newItem.supplier_id || 'none'} onValueChange={handleItemSupplierSelect}>
                    <SelectTrigger id="supplier">
                      <SelectValue placeholder={t('suppliers.selectSupplier')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('suppliers.noSupplier')}</SelectItem>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={String(supplier.id)}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                    <p className="min-w-0 text-xs text-slate-500">{t('suppliers.assignSupplierHint')}</p>
                    <Button type="button" variant="ghost" size="sm" className="h-auto px-0 text-xs text-blue-600 hover:text-blue-700" onClick={openSupplierManager}>
                      {t('suppliers.manageSuppliers')}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="lead_time_days">{t('inventory.leadTimeDays')}</Label>
                  <Input id="lead_time_days" type="number" value={newItem.lead_time_days} onChange={(e) => setNewItem({ ...newItem, lead_time_days: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>{t('inventory.cancel')}</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{editingItem ? t('inventory.update') : t('inventory.save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      );
    }

    if (activeTab === 'orders') {
      return (
        <Button className="bg-blue-600 hover:bg-blue-700 shadow-md" onClick={() => { setOrderForm(EMPTY_ORDER_FORM); setNewOrderDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          {t('orders.newOrder')}
        </Button>
      );
    }

    if (activeTab === 'suppliers') {
      return (
        <Dialog
          open={isSupplierDialogOpen}
          onOpenChange={(open) => {
            setIsSupplierDialogOpen(open);
            if (!open) {
              setEditingSupplier(null);
              resetSupplierForm();
            }
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 shadow-md">
              <Plus className="w-4 h-4 mr-2" />
              {t('suppliers.addSupplier')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSupplier ? t('suppliers.editSupplier') : t('suppliers.newSupplier')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSupplierSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="supplier_name">{t('suppliers.name')}</Label>
                <Input
                  id="supplier_name"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier((supplier) => ({ ...supplier, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier_lead_time">{t('suppliers.leadTimeDays')}</Label>
                <Input
                  id="supplier_lead_time"
                  type="number"
                  min="0"
                  value={newSupplier.lead_time_days}
                  onChange={(e) => setNewSupplier((supplier) => ({ ...supplier, lead_time_days: e.target.value }))}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsSupplierDialogOpen(false)}>
                  {t('inventory.cancel')}
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingSupplier ? t('inventory.update') : t('inventory.save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      );
    }

    return null;
  };

  // ────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────
  return (
    <Layout>
      <div className="p-3 sm:p-6 md:p-8 min-h-full">
        <div className="max-w-7xl mx-auto">

          {/* ═══ HEADER ═══ */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('stock.title')}</h1>
              <p className="text-slate-500 mt-1">{t('stock.subtitle')}</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {renderHeaderAction()}
            </div>
          </div>

          {/* Delete Item Confirmation */}
          <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('inventory.deleteProduct')}</AlertDialogTitle>
                <AlertDialogDescription>{t('inventory.deleteProductDesc')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('inventory.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteItemMutation.mutate(deleteItemId)} className="bg-red-600 hover:bg-red-700">{t('inventory.delete')}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={!!deleteSupplierId} onOpenChange={() => setDeleteSupplierId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('suppliers.deleteSupplier')}</AlertDialogTitle>
                <AlertDialogDescription>{t('suppliers.deleteSupplierDesc')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('inventory.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteSupplierMutation.mutate(deleteSupplierId)} className="bg-red-600 hover:bg-red-700">
                  {t('inventory.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* ═══ ZONE 1: ATTENTION STRIP ═══ */}
          {lowStockItems.length === 0 && upcomingStockAlerts.length === 0 ? (
            <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">{t('stock.allStockOk')}</span>
            </div>
          ) : (
            <div className="mb-4 rounded-lg border border-slate-200 bg-white overflow-hidden">
              {lowStockItems.length > 0 && (
                <div className={`${hasCritical ? 'bg-red-50' : 'bg-orange-50'}`}>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`w-4 h-4 ${hasCritical ? 'text-red-600' : 'text-orange-600'}`} />
                      <span className={`text-sm font-semibold ${hasCritical ? 'text-red-700' : 'text-orange-700'}`}>
                        {t('stock.itemsBelowReorder', { count: lowStockItems.length })}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleOrderAll}
                      disabled={syncOrderListMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-xs"
                    >
                      <ShoppingBag className="w-3.5 h-3.5 mr-1" />
                      {t('stock.addAllToOrderList')}
                    </Button>
                  </div>
                  <div className="px-4 pb-4 space-y-2">
                    {lowStockItems.map(item => {
                      const isCritical = isCriticalStock(item);
                      const purchasePlan = getPurchasePlan(
                        item,
                        Math.ceil((item.reorder_point - item.current_quantity) + ((item.reorder_point - item.minimum_quantity) * 0.5)),
                      );
                      const isInOrderList = suggestionByInventoryItemId.has(String(item.id));
                      return (
                        <div key={item.id} className={`flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between py-2 px-3 rounded-md border ${isCritical ? 'bg-red-100/60 border-red-100' : 'bg-white/80 border-white/70'}`}>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 flex-1 min-w-0">
                            <div className="min-w-0 w-full sm:w-40">
                              <span className="font-medium text-sm text-slate-900 truncate block">{item.name}</span>
                              {purchasePlan.hasPurchaseConfig && (
                                <span className="text-xs text-slate-500">
                                  {purchasePlan.summary}
                                </span>
                              )}
                            </div>
                            {isCritical && <Badge className="bg-red-100 text-red-700 border-red-200 border text-xs">{t('inventory.critical')}</Badge>}
                            <StockBar current={item.current_quantity} reorder={item.reorder_point} unit={item.unit} />
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleQuickOrder(item)}
                            disabled={syncOrderListMutation.isPending}
                            className="ml-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                          >
                            {isInOrderList ? t('stock.updateOrderList') : t('stock.addToOrderList')}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {upcomingStockAlerts.length > 0 && (
                <div className={`bg-amber-50 ${lowStockItems.length > 0 ? 'border-t border-slate-200' : ''}`}>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-800">
                        {t('stock.futureReorderAlert', { count: upcomingStockAlerts.length, days: FORECAST_HORIZON_DAYS })}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleProjectedOrderAll}
                      disabled={syncOrderListMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-xs"
                    >
                      <ShoppingBag className="w-3.5 h-3.5 mr-1" />
                      {t('stock.addAllToOrderList')}
                    </Button>
                  </div>
                  <div className="px-4 pb-4 space-y-2">
                    {upcomingStockAlerts.map(item => {
                      const severity = getProjectedStockSeverity(item);
                      const purchasePlan = getPurchasePlan(
                        item,
                        Math.ceil((item.reorder_point - item.stock_after) + ((item.reorder_point - item.minimum_quantity) * 0.5)),
                      );
                      const isInOrderList = suggestionByInventoryItemId.has(String(item.id));
                      return (
                        <div key={item.id} className={`flex items-center justify-between gap-3 text-sm rounded-md px-3 py-2 border ${severity.container}`}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 ${severity.icon}`} />
                            <div className="min-w-0">
                              <div className="font-medium text-slate-700">{item.name}</div>
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                <Badge className={`border text-[11px] ${severity.label}`}>
                                  {item.reorder_breach_date
                                    ? t('stock.futureBelowReorderOn', { date: formatDate(item.reorder_breach_date) })
                                    : t('stock.futureBelowReorderSoon')}
                                </Badge>
                                {Number.isFinite(item.days_until_reorder_breach) && (
                                  <Badge className="border border-slate-200 bg-white text-[11px] text-slate-600">
                                    {t('stock.futureBelowReorderInDays', { count: item.days_until_reorder_breach })}
                                  </Badge>
                                )}
                              </div>
                              {purchasePlan.hasPurchaseConfig && (
                                <div className="mt-1 text-xs text-slate-500">{purchasePlan.summary}</div>
                              )}
                            </div>
                          </div>
                          <div className="hidden md:flex items-center gap-4 text-xs text-slate-500">
                            <span>{t('stock.current')}: <span className="font-medium text-slate-700">{formatInventoryNumber(item.current_quantity)} {item.unit}</span></span>
                            <span>-{formatInventoryNumber(item.projected_consumption)} {item.unit}</span>
                            <span className={`font-semibold ${severity.value}`}>
                              {t('stock.projectedAfter')}: {formatInventoryNumber(item.stock_after)} {item.unit}
                            </span>
                            <span className="text-slate-400">{t('stock.reorderAt')}: {formatInventoryNumber(item.reorder_point)} {item.unit}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleProjectedQuickOrder(item)}
                            disabled={syncOrderListMutation.isPending}
                            className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                          >
                            {isInOrderList ? t('stock.updateOrderList') : t('stock.addToOrderList')}
                          </Button>
                        </div>
                      );
                    })}
                    <p className="text-xs text-amber-600 opacity-70">
                      {t('stock.stockForecastBasis', { days: FORECAST_HORIZON_DAYS })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ ZONE 2: TABS ═══ */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4 sm:space-y-6">
            <TabsList className="bg-white shadow-sm">
              <TabsTrigger value="products">
                <Package className="w-4 h-4 mr-2" />
                {t('stock.productsTab')}
              </TabsTrigger>
              <TabsTrigger value="forecast">
                <BarChart3 className="w-4 h-4 mr-2" />
                {t('stock.forecastTab')}
              </TabsTrigger>
              <TabsTrigger value="orders">
                <ShoppingBag className="w-4 h-4 mr-2" />
                {t('stock.ordersTab')}
              </TabsTrigger>
              <TabsTrigger value="suppliers">
                <Building2 className="w-4 h-4 mr-2" />
                {t('stock.suppliersTab')}
              </TabsTrigger>
              <TabsTrigger value="losses">
                <Trash2 className="w-4 h-4 mr-2" />
                {t('stock.lossesTab')}
              </TabsTrigger>
            </TabsList>

            {/* ─── TAB: FORECAST ─── */}
            <TabsContent value="forecast" className="space-y-4">
              <ProductForecastChart
                options={forecastItemOptions}
                selectedKey={effectiveForecastItemKey}
                onSelectedKeyChange={setSelectedForecastItemKey}
                chartData={forecastItemChartData}
                t={t}
              />
              <ProductForecastPanel forecastResponse={forecastResponse} t={t} />
            </TabsContent>

            {/* ─── TAB: PRODUCTS ─── */}
            <TabsContent value="products">
              {(() => {
                const missingCostCount = inventory.filter(
                  isMissingCost
                ).length;
                if (missingCostCount === 0) return null;
                return (
                  <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <div className="flex-1">
                      {missingCostCount === 1
                        ? t('recipes.missingCostBannerOne')
                        : t('recipes.missingCostBannerMany', { count: missingCostCount })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setFilterCategory(filterCategory === '__missing_cost__' ? 'all' : '__missing_cost__')}
                      className="rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700"
                    >
                      {filterCategory === '__missing_cost__'
                        ? t('recipes.costGapShowAll')
                        : t('recipes.costGapShowOnly')}
                    </button>
                  </div>
                );
              })()}
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="search"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder={t('inventory.searchPlaceholder')}
                    className="pl-9 bg-white shadow-sm"
                  />
                </div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-full sm:w-64 bg-white shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('inventory.allCategories')}</SelectItem>
                    <SelectItem value="__missing_cost__">{t('recipes.missingCostFilter')}</SelectItem>
                    <SelectItem value="Frische Zutaten">{t('inventory.freshIngredients')}</SelectItem>
                    <SelectItem value="Trockenwaren">{t('inventory.dryGoods')}</SelectItem>
                    <SelectItem value="Getränke">{t('inventory.beverages')}</SelectItem>
                    <SelectItem value="Verpackung">{t('inventory.packaging')}</SelectItem>
                    <SelectItem value="Sonstiges">{t('inventory.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <SortableHeader label={t('inventory.product')} sortKey="product" sortConfig={productsSort} onSort={handleProductsSort} />
                          <SortableHeader label={t('inventory.stock')} sortKey="stock" sortConfig={productsSort} onSort={handleProductsSort} />
                          <SortableHeader label={t('inventory.category')} sortKey="category" sortConfig={productsSort} onSort={handleProductsSort} />
                          <SortableHeader label={t('inventory.supplierCol')} sortKey="supplier" sortConfig={productsSort} onSort={handleProductsSort} />
                          <SortableHeader label={t('inventory.costPerUnit')} sortKey="cost" sortConfig={productsSort} onSort={handleProductsSort} />
                          <TableHead className="text-right">{t('inventory.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isLoading ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell></TableRow>
                        ) : sortedInventory.length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">{t('inventory.noProducts')}</TableCell></TableRow>
                        ) : (
                          sortedInventory.map((item) => {
                            const status = getStockStatus(item);
                            return (
                              <TableRow key={item.id} className="hover:bg-slate-50">
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div>
                                      <div className="font-medium">{item.name}</div>
                                      {item.article_number && (
                                        <div className="text-xs text-slate-400">{t('inventory.articleNumber')}: {item.article_number}</div>
                                      )}
                                    </div>
                                    <Badge className={`${status.color} border text-xs`}>{status.label}</Badge>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <StockBar current={item.current_quantity} reorder={item.reorder_point} unit={item.unit} />
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm text-slate-600">{t(`inventory.categories.${item.category}`, item.category)}</span>
                                </TableCell>
                                <TableCell className="text-slate-600">{item.supplier_name || item.supplier || '-'}</TableCell>
                                <TableCell className="text-slate-600">
                                  {getDerivedCostPerUnit(item) !== null ? (
                                    <div>
                                      <div>{formatInventoryNumber(getDerivedCostPerUnit(item))} / {item.unit}</div>
                                      {item.purchase_unit_quantity && (
                                        <div className="text-xs text-slate-400">
                                          {formatInventoryNumber(item.purchase_unit_quantity)} {item.unit}
                                          {item.purchase_unit_cost ? ` • €${formatInventoryNumber(item.purchase_unit_cost)}` : ''}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                                      className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                                    >
                                      <AlertTriangle className="h-3 w-3" />
                                      {t('recipes.noPriceSet')}
                                    </button>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleEdit(item)}>
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteItemId(item.id)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile: stacked card list (same data + handlers as the desktop table) */}
                  <div className="md:hidden divide-y divide-slate-100">
                    {isLoading ? (
                      <div className="py-8 text-center text-sm text-slate-500">{t('inventory.loading')}</div>
                    ) : sortedInventory.length === 0 ? (
                      <div className="py-8 text-center text-sm text-slate-500">{t('inventory.noProducts')}</div>
                    ) : (
                      sortedInventory.map((item) => {
                        const status = getStockStatus(item);
                        const cost = getDerivedCostPerUnit(item);
                        return (
                          <div key={item.id} className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-medium text-slate-900 truncate">{item.name}</div>
                                {item.article_number && (
                                  <div className="text-xs text-slate-400">{t('inventory.articleNumber')}: {item.article_number}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Badge className={`${status.color} border text-xs`}>{status.label}</Badge>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleEdit(item)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteItemId(item.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <span className="text-xs text-slate-500">{t('inventory.stock')}</span>
                              <StockBar current={item.current_quantity} reorder={item.reorder_point} unit={item.unit} />
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span className="text-xs text-slate-500">{t('inventory.category')}</span>
                              <span className="text-sm text-slate-600 text-right">{t(`inventory.categories.${item.category}`, item.category)}</span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span className="text-xs text-slate-500">{t('inventory.supplierCol')}</span>
                              <span className="text-sm text-slate-600 text-right">{item.supplier_name || item.supplier || '-'}</span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span className="text-xs text-slate-500">{t('inventory.costPerUnit')}</span>
                              {cost !== null ? (
                                <div className="text-right text-sm text-slate-600">
                                  <div>{formatInventoryNumber(cost)} / {item.unit}</div>
                                  {item.purchase_unit_quantity && (
                                    <div className="text-xs text-slate-400">
                                      {formatInventoryNumber(item.purchase_unit_quantity)} {item.unit}
                                      {item.purchase_unit_cost ? ` • €${formatInventoryNumber(item.purchase_unit_cost)}` : ''}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                                  className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  {t('recipes.noPriceSet')}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── TAB: ORDERS ─── */}
            <TabsContent value="orders">
              {/* Filter pills */}
              <div className="flex gap-2 mb-4">
                {['suggestions', 'active', 'history'].map(f => (
                  <button
                    key={f}
                    onClick={() => setOrdersFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      ordersFilter === f
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {t(`stock.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
                  </button>
                ))}
              </div>

              {ordersFilter === 'suggestions' && (
                <div className="space-y-4">
                  {loadingOrders ? (
                    <Card className="border-0 shadow-md">
                      <CardContent className="py-8 text-center text-slate-500">{t('inventory.loading')}</CardContent>
                    </Card>
                  ) : orderListGroups.length === 0 ? (
                    <Card className="border-0 shadow-md">
                      <CardContent className="py-12 text-center">
                        <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 text-sm">{t('stock.orderListEmpty')}</p>
                      </CardContent>
                    </Card>
                  ) : (
                    sortedOrderListGroups.map((group) => (
                      <Card key={group.key} className="border-0 shadow-md">
                        <CardContent className="p-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">{group.supplierLabel}</h3>
                              <p className="text-sm text-slate-500">
                                {t('stock.itemsInOrderList', { count: group.items.length })} · {t('orders.total')}: EUR {formatInventoryNumber(group.totalCost)}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">{t('stock.orderListHint')}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleDownloadOrderListPdf(group)} className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50">
                                <Download className="w-3.5 h-3.5 mr-1" />
                                {t('stock.downloadPdf')}
                              </Button>
                              <Button size="sm" onClick={() => markOrderListOrderedMutation.mutate(group)} disabled={markOrderListOrderedMutation.isPending} className="text-xs bg-orange-600 hover:bg-orange-700">
                                {t('stock.markListOrdered')}
                              </Button>
                            </div>
                          </div>
                          <div className="hidden md:block overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-50">
                                  <SortableHeader label={t("orders.product")} sortKey="product" sortConfig={shoppingCartSort} onSort={handleShoppingCartSort} />
                                  <SortableHeader label={t("orders.articleNumber")} sortKey="articleNumber" sortConfig={shoppingCartSort} onSort={handleShoppingCartSort} />
                                  <SortableHeader label={t("orders.quantity")} sortKey="quantity" sortConfig={shoppingCartSort} onSort={handleShoppingCartSort} />
                                  <SortableHeader label={t("orders.delivery")} sortKey="delivery" sortConfig={shoppingCartSort} onSort={handleShoppingCartSort} />
                                  <SortableHeader label={t("orders.cost")} sortKey="cost" sortConfig={shoppingCartSort} onSort={handleShoppingCartSort} />
                                  <TableHead className="text-right">{t("orders.action")}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.items.map((order) => (
                                  <TableRow key={order.id} className="hover:bg-slate-50">
                                    <TableCell className="font-medium">
                                      <div>{order.inventory_item_name}</div>
                                      {order.notes && <div className="text-xs text-slate-400 line-clamp-1">{order.notes}</div>}
                                    </TableCell>
                                    <TableCell className="text-slate-600">{order.article_number || '-'}</TableCell>
                                    <TableCell className="font-semibold">{formatOrderListQuantity(order)}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{formatDate(order.expected_delivery_date)}</TableCell>
                                    <TableCell className="text-sm text-slate-600">EUR {formatInventoryNumber(order.estimated_cost || 0)}</TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex justify-end gap-1">
                                        <Button size="sm" variant="outline" onClick={() => handleMarkOrdered(order)} className="text-xs text-orange-600 border-orange-200 hover:bg-orange-50">
                                          {t("orders.markAsOrdered")}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                          {t("orders.cancel")}
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          {/* Mobile: stacked card list */}
                          <div className="md:hidden divide-y divide-slate-100 border-t border-slate-100">
                            {group.items.map((order) => (
                              <div key={order.id} className="px-4 py-3">
                                <div className="font-medium text-slate-900">{order.inventory_item_name}</div>
                                {order.notes && <div className="text-xs text-slate-400 line-clamp-1">{order.notes}</div>}
                                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                                  <span className="text-xs text-slate-500">{t('orders.articleNumber')}</span>
                                  <span className="text-slate-600 text-right">{order.article_number || '-'}</span>
                                  <span className="text-xs text-slate-500">{t('orders.quantity')}</span>
                                  <span className="font-semibold text-slate-700 text-right">{formatOrderListQuantity(order)}</span>
                                  <span className="text-xs text-slate-500">{t('orders.delivery')}</span>
                                  <span className="text-slate-600 text-right">{formatDate(order.expected_delivery_date)}</span>
                                  <span className="text-xs text-slate-500">{t('orders.cost')}</span>
                                  <span className="text-slate-600 text-right">EUR {formatInventoryNumber(order.estimated_cost || 0)}</span>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <Button size="sm" variant="outline" onClick={() => handleMarkOrdered(order)} className="text-xs text-orange-600 border-orange-200 hover:bg-orange-50">
                                    {t("orders.markAsOrdered")}
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                    {t("orders.cancel")}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}

              {ordersFilter !== 'suggestions' && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <SortableHeader label={t("orders.product")} sortKey="product" sortConfig={ordersTableSort} onSort={handleOrdersTableSort} />
                          <SortableHeader label={t("orders.articleNumber")} sortKey="articleNumber" sortConfig={ordersTableSort} onSort={handleOrdersTableSort} />
                          <SortableHeader label={t("orders.quantity")} sortKey="quantity" sortConfig={ordersTableSort} onSort={handleOrdersTableSort} />
                          <SortableHeader label={t("orders.supplier")} sortKey="supplier" sortConfig={ordersTableSort} onSort={handleOrdersTableSort} />
                          <SortableHeader label={t("orders.orderedOn")} sortKey="orderedOn" sortConfig={ordersTableSort} onSort={handleOrdersTableSort} />
                          <SortableHeader label={t("orders.delivery")} sortKey="delivery" sortConfig={ordersTableSort} onSort={handleOrdersTableSort} />
                          <SortableHeader label={t("orders.status")} sortKey="status" sortConfig={ordersTableSort} onSort={handleOrdersTableSort} />
                          <TableHead className="text-right">{t("orders.action")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingOrders ? (
                          <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell></TableRow>
                        ) : sortedFilteredOrders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-12">
                              <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                              <p className="text-slate-500 text-sm">{t('stock.noOrdersInFilter')}</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedFilteredOrders.map((order) => (
                            <TableRow key={order.id} className="hover:bg-slate-50">
                              <TableCell className="font-medium">{order.inventory_item_name}</TableCell>
                              <TableCell className="text-slate-600">{order.article_number || '-'}</TableCell>
                              <TableCell>
                                {(() => {
                                  if (order.purchase_units_ordered && order.package_label) {
                                    return (
                                      <div>
                                        <div className="font-semibold">
                                          {formatInventoryNumber(order.purchase_units_ordered)} x {order.package_label}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                          {formatInventoryNumber(order.suggested_quantity)} {order.unit}
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <span className="font-semibold">
                                      {formatInventoryNumber(order.suggested_quantity)} {order.unit}
                                    </span>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="text-slate-600">{order.supplier_name || order.supplier || '-'}</TableCell>
                              <TableCell className="text-sm text-slate-600">{formatDate(order.order_date)}</TableCell>
                              <TableCell className="text-sm text-slate-600">{formatDate(order.expected_delivery_date)}</TableCell>
                              <TableCell>
                                <Badge className={`${getStatusColor(order.status)} border`}>{order.status}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  {/* Suggestion: Mark Ordered, Cancel */}
                                  {order.status === t("orders.statusSuggestion") && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkOrdered(order)} className="text-xs text-orange-600 border-orange-200 hover:bg-orange-50">
                                        {t("orders.markAsOrdered")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                        {t("orders.cancel")}
                                      </Button>
                                    </>
                                  )}
                                  {/* Ordered: Mark Arriving, Mark Delivered, Cancel */}
                                  {order.status === t("orders.statusOrdered") && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkArriving(order)} className="text-xs text-teal-600 border-teal-200 hover:bg-teal-50">
                                        {t("orders.markArriving")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkDelivered(order)} className="text-xs text-green-600 border-green-200 hover:bg-green-50">
                                        {t("orders.markAsDelivered")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                        {t("orders.cancel")}
                                      </Button>
                                    </>
                                  )}
                                  {/* Arriving: Mark Delivered, Cancel */}
                                  {order.status === t("orders.statusArriving") && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkDelivered(order)} className="text-xs text-green-600 border-green-200 hover:bg-green-50">
                                        {t("orders.markAsDelivered")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                        {t("orders.cancel")}
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile: stacked card list (same status-conditional actions as the desktop table) */}
                  <div className="md:hidden divide-y divide-slate-100">
                    {loadingOrders ? (
                      <div className="py-8 text-center text-sm text-slate-500">{t('inventory.loading')}</div>
                    ) : sortedFilteredOrders.length === 0 ? (
                      <div className="py-12 text-center">
                        <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 text-sm">{t('stock.noOrdersInFilter')}</p>
                      </div>
                    ) : (
                      sortedFilteredOrders.map((order) => (
                        <div key={order.id} className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-medium text-slate-900 min-w-0 truncate">{order.inventory_item_name}</div>
                            <Badge className={`${getStatusColor(order.status)} border flex-shrink-0`}>{order.status}</Badge>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                            <span className="text-xs text-slate-500">{t('orders.articleNumber')}</span>
                            <span className="text-slate-600 text-right">{order.article_number || '-'}</span>
                            <span className="text-xs text-slate-500">{t('orders.quantity')}</span>
                            <span className="text-right">
                              {(() => {
                                if (order.purchase_units_ordered && order.package_label) {
                                  return (
                                    <span>
                                      <span className="font-semibold">{formatInventoryNumber(order.purchase_units_ordered)} x {order.package_label}</span>
                                      <span className="block text-xs text-slate-500">{formatInventoryNumber(order.suggested_quantity)} {order.unit}</span>
                                    </span>
                                  );
                                }
                                return <span className="font-semibold">{formatInventoryNumber(order.suggested_quantity)} {order.unit}</span>;
                              })()}
                            </span>
                            <span className="text-xs text-slate-500">{t('orders.supplier')}</span>
                            <span className="text-slate-600 text-right">{order.supplier_name || order.supplier || '-'}</span>
                            <span className="text-xs text-slate-500">{t('orders.orderedOn')}</span>
                            <span className="text-slate-600 text-right">{formatDate(order.order_date)}</span>
                            <span className="text-xs text-slate-500">{t('orders.delivery')}</span>
                            <span className="text-slate-600 text-right">{formatDate(order.expected_delivery_date)}</span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {order.status === t("orders.statusSuggestion") && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleMarkOrdered(order)} className="text-xs text-orange-600 border-orange-200 hover:bg-orange-50">{t("orders.markAsOrdered")}</Button>
                                <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">{t("orders.cancel")}</Button>
                              </>
                            )}
                            {order.status === t("orders.statusOrdered") && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleMarkArriving(order)} className="text-xs text-teal-600 border-teal-200 hover:bg-teal-50">{t("orders.markArriving")}</Button>
                                <Button size="sm" variant="outline" onClick={() => handleMarkDelivered(order)} className="text-xs text-green-600 border-green-200 hover:bg-green-50">{t("orders.markAsDelivered")}</Button>
                                <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">{t("orders.cancel")}</Button>
                              </>
                            )}
                            {order.status === t("orders.statusArriving") && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleMarkDelivered(order)} className="text-xs text-green-600 border-green-200 hover:bg-green-50">{t("orders.markAsDelivered")}</Button>
                                <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">{t("orders.cancel")}</Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            {/* ─── TAB: SUPPLIERS ─── */}
            <TabsContent value="suppliers">
              <div className="mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{t('suppliers.title')}</h2>
                  <p className="text-sm text-slate-500">{t('suppliers.subtitle')}</p>
                </div>
              </div>

              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <SortableHeader label={t('suppliers.name')} sortKey="name" sortConfig={suppliersSort} onSort={handleSuppliersSort} />
                          <SortableHeader label={t('suppliers.leadTimeDays')} sortKey="leadTime" sortConfig={suppliersSort} onSort={handleSuppliersSort} />
                          <SortableHeader label={t('suppliers.linkedProducts')} sortKey="products" sortConfig={suppliersSort} onSort={handleSuppliersSort} />
                          <SortableHeader label={t('suppliers.activeOrders')} sortKey="activeOrders" sortConfig={suppliersSort} onSort={handleSuppliersSort} />
                          <TableHead className="text-right">{t('inventory.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingSuppliers ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell>
                          </TableRow>
                        ) : sortedSupplierStats.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="py-12 text-center">
                              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                              <p className="text-slate-600">{t('suppliers.noSuppliers')}</p>
                              <p className="mt-2 text-sm text-slate-400">{t('suppliers.noSuppliersDesc')}</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedSupplierStats.map((supplier) => (
                            <TableRow key={supplier.id} className="hover:bg-slate-50">
                              <TableCell className="font-medium">{supplier.name}</TableCell>
                              <TableCell className="text-slate-600">
                                {supplier.lead_time_days != null ? t('suppliers.days', { count: supplier.lead_time_days }) : '—'}
                              </TableCell>
                              <TableCell className="text-slate-600">{supplier.productCount}</TableCell>
                              <TableCell className="text-slate-600">{supplier.activeOrderCount}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50 hover:text-blue-700" onClick={() => handleEditSupplier(supplier)}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteSupplierId(supplier.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile: stacked card list */}
                  <div className="md:hidden divide-y divide-slate-100">
                    {loadingSuppliers ? (
                      <div className="py-8 text-center text-sm text-slate-500">{t('inventory.loading')}</div>
                    ) : sortedSupplierStats.length === 0 ? (
                      <div className="py-12 text-center">
                        <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-600">{t('suppliers.noSuppliers')}</p>
                        <p className="mt-2 text-sm text-slate-400">{t('suppliers.noSuppliersDesc')}</p>
                      </div>
                    ) : (
                      sortedSupplierStats.map((supplier) => (
                        <div key={supplier.id} className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-medium text-slate-900 min-w-0 truncate">{supplier.name}</div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50 hover:text-blue-700" onClick={() => handleEditSupplier(supplier)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteSupplierId(supplier.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                            <span className="text-xs text-slate-500">{t('suppliers.leadTimeDays')}</span>
                            <span className="text-slate-600 text-right">{supplier.lead_time_days != null ? t('suppliers.days', { count: supplier.lead_time_days }) : '—'}</span>
                            <span className="text-xs text-slate-500">{t('suppliers.linkedProducts')}</span>
                            <span className="text-slate-600 text-right">{supplier.productCount}</span>
                            <span className="text-xs text-slate-500">{t('suppliers.activeOrders')}</span>
                            <span className="text-slate-600 text-right">{supplier.activeOrderCount}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── TAB: LOSSES ─── */}
            <TabsContent value="losses">
              {/* Weekly summary */}
              <div className="mb-4 px-4 py-3 rounded-lg bg-slate-50 border border-slate-200">
                <span className="text-sm text-slate-600">
                  {t('stock.weekSummary', { count: weekLossSummary.count, cost: weekLossSummary.cost.toFixed(2) })}
                </span>
              </div>

              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <SortableHeader label={t('inventory.date')} sortKey="date" sortConfig={lossesSort} onSort={handleLossesSort} />
                          <SortableHeader label={t('inventory.product')} sortKey="product" sortConfig={lossesSort} onSort={handleLossesSort} />
                          <SortableHeader label={t('inventory.quantity')} sortKey="quantity" sortConfig={lossesSort} onSort={handleLossesSort} />
                          <SortableHeader label={t('inventory.reason')} sortKey="reason" sortConfig={lossesSort} onSort={handleLossesSort} />
                          <SortableHeader label={t('inventory.shift')} sortKey="shift" sortConfig={lossesSort} onSort={handleLossesSort} />
                          <SortableHeader label={t('inventory.cost')} sortKey="cost" sortConfig={lossesSort} onSort={handleLossesSort} />
                          <SortableHeader label={t('inventory.reportedBy')} sortKey="reportedBy" sortConfig={lossesSort} onSort={handleLossesSort} />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingLosses ? (
                          <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell></TableRow>
                        ) : sortedLosses.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-12">
                              <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                              <p className="text-slate-500">{t('inventory.noLosses')}</p>
                              <p className="text-sm text-slate-400 mt-2">{t('inventory.noLossesDesc')}</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedLosses.map((loss) => (
                            <TableRow key={loss.id} className="hover:bg-slate-50">
                              <TableCell className="font-medium">{format(new Date(loss.date), 'dd.MM.yyyy')}</TableCell>
                              <TableCell>{loss.inventory_item_name}</TableCell>
                              <TableCell>
                                <span className="font-semibold text-red-600">-{loss.quantity} {loss.unit}</span>
                              </TableCell>
                              <TableCell>
                                <Badge className={reasonColors[loss.reason] || 'bg-slate-100 text-slate-700'}>
                                  {reasonLabels[loss.reason] || loss.reason}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-600">
                                  {loss.shift === 'Mittag' ? t('inventory.lunch') : loss.shift === 'Abend' ? t('inventory.dinner') : loss.shift}
                                </span>
                              </TableCell>
                              <TableCell className="font-semibold text-red-600">{loss.cost?.toFixed(2) || '0.00'}</TableCell>
                              <TableCell className="text-slate-600">{loss.reported_by || '-'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile: stacked card list (read-only) */}
                  <div className="md:hidden divide-y divide-slate-100">
                    {loadingLosses ? (
                      <div className="py-8 text-center text-sm text-slate-500">{t('inventory.loading')}</div>
                    ) : sortedLosses.length === 0 ? (
                      <div className="py-12 text-center">
                        <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">{t('inventory.noLosses')}</p>
                        <p className="text-sm text-slate-400 mt-2">{t('inventory.noLossesDesc')}</p>
                      </div>
                    ) : (
                      sortedLosses.map((loss) => (
                        <div key={loss.id} className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-medium text-slate-900 min-w-0 truncate">{loss.inventory_item_name}</div>
                            <span className="text-xs text-slate-500 flex-shrink-0">{format(new Date(loss.date), 'dd.MM.yyyy')}</span>
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <Badge className={reasonColors[loss.reason] || 'bg-slate-100 text-slate-700'}>
                              {reasonLabels[loss.reason] || loss.reason}
                            </Badge>
                            <span className="font-semibold text-red-600">-{loss.quantity} {loss.unit}</span>
                          </div>
                          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                            <span className="text-xs text-slate-500">{t('inventory.shift')}</span>
                            <span className="text-slate-600 text-right">{loss.shift === 'Mittag' ? t('inventory.lunch') : loss.shift === 'Abend' ? t('inventory.dinner') : loss.shift}</span>
                            <span className="text-xs text-slate-500">{t('inventory.cost')}</span>
                            <span className="font-semibold text-red-600 text-right">{loss.cost?.toFixed(2) || '0.00'}</span>
                            <span className="text-xs text-slate-500">{t('inventory.reportedBy')}</span>
                            <span className="text-slate-600 text-right">{loss.reported_by || '-'}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ═══ NEW ORDER DIALOG ═══ */}
      <Dialog open={newOrderDialog} onOpenChange={setNewOrderDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("orders.newOrder")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>{t("orders.product")}</Label>
              <Select
                value={orderForm.inventory_item_id ? String(orderForm.inventory_item_id) : 'manual'}
                onValueChange={handleInventoryItemSelect}
              >
                <SelectTrigger>
                  <SelectValue>
                    {orderForm.inventory_item_id
                      ? inventory.find(i => i.id === orderForm.inventory_item_id)?.name || String(orderForm.inventory_item_id)
                      : t("orders.manualEntry")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t("orders.manualEntry")}</SelectItem>
                  {inventory.map(item => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.name}{item.article_number ? ` · ${item.article_number}` : ''} ({item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("orders.productName")} *</Label>
                <Input value={orderForm.inventory_item_name} onChange={(e) => setOrderForm(f => ({ ...f, inventory_item_name: e.target.value }))} placeholder={t("orders.productNamePlaceholder")} />
              </div>
              <div className="space-y-2">
                <Label>{t("orders.articleNumber")}</Label>
                <Input
                  value={orderForm.article_number}
                  onChange={(e) => setOrderForm(f => ({ ...f, article_number: e.target.value }))}
                  placeholder={t("orders.articleNumberPlaceholder")}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{selectedOrderItem?.purchase_unit_quantity ? t("orders.orderCount") : t("orders.quantity")} *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.001"
                    value={orderForm.suggested_quantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOrderForm((f) => {
                        if (!selectedOrderItem || !value) {
                          return { ...f, suggested_quantity: value };
                        }
                        const draft = getOrderDraft(selectedOrderItem, parseLocaleNumber(value));
                        return {
                          ...f,
                          suggested_quantity: value,
                          estimated_cost: draft.estimatedCost.toFixed(2),
                        };
                      });
                    }}
                  />
                  {selectedOrderDraft?.usesPurchaseUnits && (
                    <p className="text-xs text-slate-500">
                      {selectedOrderDraft.summary} · {formatInventoryNumber(selectedOrderDraft.coverageQuantity)} {orderForm.unit}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t("orders.packageLabel")}</Label>
                  <Input
                    value={orderForm.package_label}
                    onChange={(e) => setOrderForm(f => ({ ...f, package_label: e.target.value }))}
                    placeholder={t("orders.packageLabelPlaceholder")}
                  />
                </div>
            </div>

            <div className="space-y-2">
              <Label>{t("orders.unit")} *</Label>
              <Input value={orderForm.unit} onChange={(e) => setOrderForm(f => ({ ...f, unit: e.target.value }))} placeholder="kg, L, pcs..." />
            </div>

            <div className="space-y-2">
              <Label>{t("orders.supplier")}</Label>
              <Select value={orderForm.supplier_id || 'none'} onValueChange={handleOrderSupplierSelect}>
                <SelectTrigger>
                  <SelectValue placeholder={t('suppliers.selectSupplier')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('suppliers.noSupplier')}</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={String(supplier.id)}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="min-w-0 text-xs text-slate-500">{t('suppliers.orderSupplierHint')}</p>
                <Button type="button" variant="ghost" size="sm" className="h-auto px-0 text-xs text-blue-600 hover:text-blue-700" onClick={openSupplierManager}>
                  {t('suppliers.manageSuppliers')}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("orders.orderedOn")}</Label>
                <Input
                  type="date"
                  value={orderForm.order_date}
                  onChange={(e) => {
                    const nextOrderDate = e.target.value;
                    setOrderForm((form) => {
                      const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(form.supplier_id));
                      return {
                        ...form,
                        order_date: nextOrderDate,
                        expected_delivery_date:
                          getExpectedDeliveryDate(nextOrderDate, selectedSupplier?.lead_time_days) || form.expected_delivery_date,
                      };
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("orders.delivery")}</Label>
                <Input type="date" min={orderForm.order_date} value={orderForm.expected_delivery_date} onChange={(e) => setOrderForm(f => ({ ...f, expected_delivery_date: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("orders.cost")}</Label>
                <Input type="number" min="0" step="0.01" value={orderForm.estimated_cost} onChange={(e) => setOrderForm(f => ({ ...f, estimated_cost: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>{t("orders.status")}</Label>
                <Select value={orderForm.status || t("orders.statusSuggestion")} onValueChange={(v) => setOrderForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={t("orders.statusSuggestion")}>{t("orders.statusSuggestion")}</SelectItem>
                    <SelectItem value={t("orders.statusOrdered")}>{t("orders.statusOrdered")}</SelectItem>
                    <SelectItem value={t("orders.statusArriving")}>{t("orders.statusArriving")}</SelectItem>
                    <SelectItem value={t("orders.statusDelivered")}>{t("orders.statusDelivered")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("orders.notes")}</Label>
              <Textarea value={orderForm.notes} onChange={(e) => setOrderForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setNewOrderDialog(false)}>{t("orders.cancel")}</Button>
              <Button onClick={handleSubmitNewOrder} disabled={!isFormValid || createSingleOrderMutation.isPending} className="bg-blue-600 hover:bg-blue-700">
                {t("orders.saveOrder")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
